import { useDeferredValue, useState } from "react";
import {
  useDeleteEntityFile,
  useEntityFilesInfinite,
  useEntityFilesSuspense,
  type EntityFilesResponse
} from "@swivu/client-api";
import {
  Button,
  cn,
  decodeFA,
  FileCard,
  FileCardSkeleton,
  useDebounce,
  useInfiniteScroll,
  useIsGummy,
  type FileData
} from "@swivu/shared-ui";
import CommonStyles from "@swivu/entity-detail/src/CommonStyles.js";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "@heroicons/react/24/outline/index.js";
import { type TaskDetailData } from "../shared-detail-components/TaskDetailData.js";

type UnifiedMediaSurfaceProps = {
  task: TaskDetailData;
  id: number;
  api: "wf" | "public";
  entity_type: "task";
  slug?: string;
  title: string;
  canAdd?: boolean;
  onAdd?: () => void;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};

const UnifiedMediaSurface = (props: UnifiedMediaSurfaceProps) => {
  if (props.api === "wf") return <PaginatedMediaSurface {...props} />;
  return <LegacyMediaSurface {...props} />;
};

const LegacyMediaSurface = ({
  id,
  api,
  entity_type,
  slug,
  title,
  canAdd,
  onAdd,
  onAnalyze,
  advisorsEnabled,
  task
}: UnifiedMediaSurfaceProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const variables = { id, api, entity_type, slug: slug ?? "" } as const;

  const { data: files } = useEntityFilesSuspense(entity_type)({ variables });

  return (
    <MediaSurfaceContent
      files={files}
      totalCount={files.length}
      id={id}
      api={api}
      entity_type={entity_type}
      task={task}
      title={title}
      canAdd={canAdd}
      onAdd={onAdd}
      onAnalyze={onAnalyze}
      advisorsEnabled={advisorsEnabled}
      searchTerm={searchTerm}
      onSearch={setSearchTerm}
      filterLocally
    />
  );
};

const PaginatedMediaSurface = (props: UnifiedMediaSurfaceProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm.trim(), 300);
  const deferredSearch = useDeferredValue(debouncedSearch);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEntityFilesInfinite({
      variables: {
        id: props.id,
        board_id: props.task.board,
        entity_type: props.entity_type,
        ...(deferredSearch ? { search: deferredSearch } : {})
      }
    });
  const files = data.pages.flatMap((page) => page.results);
  const { lastElementRef } = useInfiniteScroll({
    isLoading: isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  });

  return (
    <MediaSurfaceContent
      {...props}
      files={files}
      totalCount={data.pages[0]?.count ?? 0}
      searchTerm={searchTerm}
      onSearch={setSearchTerm}
      loadMoreRef={lastElementRef}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
    />
  );
};

type MediaSurfaceContentProps = UnifiedMediaSurfaceProps & {
  files: EntityFilesResponse;
  totalCount: number;
  searchTerm: string;
  onSearch: (search: string) => void;
  filterLocally?: boolean;
  loadMoreRef?: (node: HTMLElement | null) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
};

const MediaSurfaceContent = ({
  files,
  totalCount,
  searchTerm,
  onSearch,
  filterLocally = false,
  loadMoreRef,
  hasNextPage,
  isFetchingNextPage,
  entity_type,
  title,
  canAdd,
  onAdd,
  onAnalyze,
  advisorsEnabled
}: MediaSurfaceContentProps) => {
  const queryClient = useQueryClient();
  const isGummy = useIsGummy();

  const deleteMutation = useDeleteEntityFile({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [`entity${entity_type}FilesWithSuspense`]
      });
      void queryClient.invalidateQueries({ queryKey: ["taskDetail"] });
    }
  });

  const handleDelete = (fileId: number | string) => {
    deleteMutation.mutate({ id: Number(fileId) });
  };

  if (files.length === 0 && !searchTerm) {
    if (canAdd && onAdd) {
      return isGummy ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add media"
          className={cn(
            "shadow-raised",
            "relative flex aspect-square w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-surface-tile px-4 py-5 text-white",
            "bg-tile-media",
            "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
            "motion-reduce:transform-none motion-reduce:transition-none",
            "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
          )}
          style={{ textShadow: "var(--text-shadow-on-color)" }}
        >
          <span aria-hidden className="top-gloss-large" />
          <i
            aria-hidden
            className={cn(
              "relative flex-none text-6xl fa-regular",
              decodeFA("photo-film-music")
            )}
          />
          <span className="relative text-center text-2xl font-black leading-tight tracking-tight">
            Add Media
          </span>
        </button>
      ) : (
        <Button
          action="secondary"
          className="flex justify-center gap-2 text-sm"
          aria-label="Add media"
          onClick={onAdd}
        >
          <i className={`fa-regular mr-2 ${decodeFA("photo-film-music")}`} />
          <span>Add Media</span>
        </Button>
      );
    }
    return null;
  }

  const filteredFiles = filterLocally ? filterFiles(files, searchTerm) : files;
  const addButton =
    canAdd && onAdd ? (
      isGummy ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add media"
          className={cn(
            "inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full",
            "shadow-raised",
            "bg-tile-media text-white",
            "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
            "motion-reduce:transform-none motion-reduce:transition-none",
            "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
          )}
          style={{ textShadow: "var(--text-shadow-on-color-strong)" }}
        >
          <PlusIcon className="size-7" strokeWidth={3} />
        </button>
      ) : (
        <Button
          action="secondary"
          onClick={onAdd}
          aria-label="Add media"
          className="h-9"
        >
          <PlusIcon className="size-4" />
        </Button>
      )
    ) : null;

  return (
    <CommonStyles
      Icon={MediaIcon}
      title="Files / Media"
      badge={totalCount}
      cardTitle={title}
      cardSubtitle="Files"
      wrapperClassName="overflow-y-auto"
      search={{
        filter: searchTerm,
        clearSearch: () => onSearch(""),
        onSearch
      }}
      actions={addButton}
    >
      <div className="grid w-full grid-cols-1 items-start gap-4 pt-4 sm:grid-cols-[repeat(auto-fill,minmax(8rem,1fr))]">
        {filteredFiles.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onDelete={handleDelete}
            onAnalyze={onAnalyze}
            isDeleting={
              deleteMutation.isPending &&
              deleteMutation.variables?.id === file.id
            }
            advisorsEnabled={advisorsEnabled}
          />
        ))}
        {isFetchingNextPage &&
          Array.from({ length: 3 }).map((_, index) => (
            <FileCardSkeleton key={`next-page-${index}`} />
          ))}
        {filteredFiles.length === 0 && searchTerm ? (
          <p className="col-span-full py-8 text-center text-sm text-mono-500 dark:text-mono-400">
            No files match your search.
          </p>
        ) : null}
        {hasNextPage ? (
          <div
            ref={loadMoreRef}
            className="col-span-full h-px w-full"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </CommonStyles>
  );
};

const filterFiles = (files: EntityFilesResponse, searchTerm: string) => {
  if (!searchTerm) return files;
  const searchLower = searchTerm.toLowerCase();
  return files.filter(
    (file) =>
      file.name?.toLowerCase().includes(searchLower) ||
      file.description?.toLowerCase().includes(searchLower) ||
      file.source?.toLowerCase().includes(searchLower)
  );
};

const MediaIcon = ({ className }: { className?: string }) => (
  <i
    className={cn(
      "fa-regular text-6xl leading-none",
      decodeFA("photo-film-music"),
      className
    )}
  />
);

export { UnifiedMediaSurface };
